import { TopicChannelThreadContinuationState } from "@redotech/cockroachdb-codegen/tables/shopper-ai/topic-channel-thread-continuation-state";
import * as z from "zod";

const paritySide = {
  marketingMessage: z
    .object({
      id: z.string(),
      conversationId: z.string(),
      direction: z.string(),
      timestamp: z.date(),
      content: z.string(),
    })
    .nullable(),
  supportRecord: z
    .object({
      kind: z.enum(["message", "draft"]),
      id: z.string(),
      channelThreadId: z.string(),
      senderType: z.string(),
      timestamp: z.date(),
      content: z.string(),
    })
    .nullable(),
};

const llmThreadAssociation = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("marketing_message"), id: z.string() }),
  z.object({
    kind: z.literal("support_message"),
    id: z.string(),
    shopperAgentTopicId: z.string(),
  }),
  z.object({
    kind: z.literal("support_draft"),
    id: z.string(),
    agentRunId: z.string(),
    draftKind: z.string(),
    status: z.string(),
    deletedAt: z.date().nullable(),
  }),
  z.object({ kind: z.literal("agent_run"), id: z.string() }),
  z.object({
    kind: z.literal("channel_thread_llm_state"),
    id: z.string(),
    agentMode: z.string(),
  }),
]);

export const getShopperAgentObservability = {
  name: "getShopperAgentObservability" as const,
  input: z.object({ id: z.string() }),
  output: z.object({
    observedAt: z.date(),
    seed: z.object({
      kind: z.enum(["marketing_conversation", "channel_thread"]),
      id: z.string(),
    }),
    team: z.object({ id: z.string(), name: z.string() }),
    marketingConversations: z.array(z.object({ id: z.string() })),
    channelThreads: z.array(z.object({ id: z.string(), channel: z.string() })),
    supportConversations: z.array(z.object({ id: z.string() })),
    shopperAgentTopics: z.array(
      z.object({
        id: z.string(),
        channelThreadId: z.string(),
        continuationState: z.enum(TopicChannelThreadContinuationState),
      }),
    ),
    channelThreadLlmStates: z.array(
      z.object({
        channelThreadId: z.string(),
        agentMode: z.string(),
        llmThreadId: z.string().nullable(),
        threadFound: z.boolean(),
        updatedAt: z.date(),
      }),
    ),
    parity: z.object({
      matchedCount: z.number(),
      differentCount: z.number(),
      missingMarketingCount: z.number(),
      missingSupportCount: z.number(),
      differences: z.array(
        z.object({
          kind: z.enum([
            "content_mismatch",
            "missing_marketing",
            "missing_support",
          ]),
          ...paritySide,
        }),
      ),
    }),
    llmThreads: z.array(
      z.object({
        id: z.string(),
        agentName: z.string(),
        createdAt: z.date(),
        updatedAt: z.date(),
        expiresAt: z.date().nullable(),
        metadata: z.array(z.object({ key: z.string(), value: z.string() })),
        associations: z.array(llmThreadAssociation),
      }),
    ),
  }),
};

export type GetShopperAgentObservabilityOutput = z.infer<
  typeof getShopperAgentObservability.output
>;
